const FEET_PER_METER = 3.281;
const INCHES_PER_FOOT = 12;
const MIN_DIM_FT = 1;
const DIMENSION_CLUSTER_TOL_FT = 6 / 12;

const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const formatDistanceFtIn = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  const sign = n < 0 ? "-" : "";
  const totalInches = Math.round(Math.abs(n) * 12);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return `${sign}${feet}'${inches}"`;
};

const sub3 = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const dot3 = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const cross3 = (a, b) => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});
const len3 = (a) => Math.hypot(a.x, a.y, a.z);
const norm3 = (a) => {
  const l = len3(a) || 1;
  return { x: a.x / l, y: a.y / l, z: a.z / l };
};

const rotatePoint = (p, ref, angle) => {
  const dx = p.x - ref.x;
  const dy = p.y - ref.y;
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return {
    x: ref.x + dx * c - dy * s,
    y: ref.y + dx * s + dy * c,
  };
};

const dedupePoints3d = (points = []) => {
  const seen = new Set();
  const out = [];
  points.forEach((point) => {
    const x = toNumber(point?.x, NaN);
    const y = toNumber(point?.y, NaN);
    const z = toNumber(point?.z, 0);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    const key = `${x.toFixed(6)}:${y.toFixed(6)}:${z.toFixed(6)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ x, y, z });
  });
  return out;
};

const dedupePoints2d = (points = []) => {
  const seen = new Set();
  const out = [];
  points.forEach((point) => {
    const x = toNumber(point?.x, NaN);
    const y = toNumber(point?.y, NaN);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    const key = `${x.toFixed(6)}:${y.toFixed(6)}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ x, y });
  });
  return out;
};

const sortPointsClockwise = (points = []) => {
  if (points.length <= 2) return points;
  const cx = points.reduce((sum, point) => sum + point.x, 0) / points.length;
  const cy = points.reduce((sum, point) => sum + point.y, 0) / points.length;
  return [...points].sort(
    (left, right) =>
      Math.atan2(left.y - cy, left.x - cx) - Math.atan2(right.y - cy, right.x - cx)
  );
};

const computePlaneNormal = (ring) => {
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < ring.length; i++) {
    const cur = ring[i];
    const nxt = ring[(i + 1) % ring.length];
    nx += (cur.y - nxt.y) * (cur.z + nxt.z);
    ny += (cur.z - nxt.z) * (cur.x + nxt.x);
    nz += (cur.x - nxt.x) * (cur.y + nxt.y);
  }
  const normal = { x: nx, y: ny, z: nz };
  return len3(normal) < 1e-9 ? { x: 0, y: 0, z: 1 } : norm3(normal);
};

const buildPlaneBasis = (ring) => {
  const origin = ring[0];
  const normal = computePlaneNormal(ring);
  let ref = { x: 1, y: 0, z: 0 };
  if (Math.abs(dot3(ref, normal)) > 0.9) ref = { x: 0, y: 1, z: 0 };
  const proj = dot3(ref, normal);
  let u = sub3(ref, {
    x: normal.x * proj,
    y: normal.y * proj,
    z: normal.z * proj,
  });
  u = norm3(u);
  const v = norm3(cross3(normal, u));
  return { origin, u, v };
};

const projectToPlane = (point, basis) => {
  const delta = sub3(
    {
      x: toNumber(point?.x),
      y: toNumber(point?.y),
      z: toNumber(point?.z),
    },
    basis.origin
  );
  return { x: dot3(delta, basis.u), y: dot3(delta, basis.v) };
};

const getBoundsFromPoints = (points = []) => {
  if (!points.length) return null;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  points.forEach((point) => {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  });
  return { minX, maxX, minY, maxY };
};

const toEdgeKey2d = (edge, decimals = 3) => {
  const a = `${edge.a.x.toFixed(decimals)},${edge.a.y.toFixed(decimals)}`;
  const b = `${edge.b.x.toFixed(decimals)},${edge.b.y.toFixed(decimals)}`;
  return a < b ? `${a}|${b}` : `${b}|${a}`;
};

const dedupeEdgesByKey2d = (edges = []) => {
  const seen = new Set();
  const out = [];
  edges.forEach((edge) => {
    const key = toEdgeKey2d(edge);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(edge);
  });
  return out;
};

const clamp01 = (value) => Math.min(1, Math.max(0, value));

const interpolateEdgePoint = (edge, t) => ({
  x: edge.a.x + (edge.b.x - edge.a.x) * t,
  y: edge.a.y + (edge.b.y - edge.a.y) * t,
  z: edge.a.z + (edge.b.z - edge.a.z) * t,
});

const getCollinearOverlap = (edge1, edge2, { lineTol = 0.08, minOverlap = 0.2 } = {}) => {
  const ax = edge1.a.x;
  const ay = edge1.a.y;
  const bx = edge1.b.x;
  const by = edge1.b.y;
  const cx = edge2.a.x;
  const cy = edge2.a.y;
  const dx = edge2.b.x;
  const dy = edge2.b.y;

  const ux = bx - ax;
  const uy = by - ay;
  const vx = dx - cx;
  const vy = dy - cy;
  const lenU = Math.hypot(ux, uy);
  const lenV = Math.hypot(vx, vy);
  if (lenU < 1e-6 || lenV < 1e-6) return null;

  const crossDir = Math.abs(ux * vy - uy * vx) / (lenU * lenV);
  if (crossDir > 0.02) return null;

  const lineDistC = Math.abs((cx - ax) * uy - (cy - ay) * ux) / lenU;
  const lineDistD = Math.abs((dx - ax) * uy - (dy - ay) * ux) / lenU;
  if (lineDistC > lineTol || lineDistD > lineTol) return null;

  const unitX = ux / lenU;
  const unitY = uy / lenU;
  const proj = (px, py) => (px - ax) * unitX + (py - ay) * unitY;

  const cProj = proj(cx, cy);
  const dProj = proj(dx, dy);
  const overlapStart = Math.max(0, Math.min(cProj, dProj));
  const overlapEnd = Math.min(lenU, Math.max(cProj, dProj));
  const overlapLen = overlapEnd - overlapStart;
  if (overlapLen < minOverlap) return null;

  return {
    pStart: { x: ax + unitX * overlapStart, y: ay + unitY * overlapStart },
    pEnd: { x: ax + unitX * overlapEnd, y: ay + unitY * overlapEnd },
  };
};

const buildPolygonEdges3d = (points = []) => {
  const unique = dedupePoints3d(points);
  if (unique.length < 2) return [];
  return unique.map((point, index) => ({
    a: point,
    b: unique[(index + 1) % unique.length],
  }));
};

const buildSetbackPoints3d = (setbacks = []) => {
  const points = setbacks.map((setback) => setback?.vertex || setback).filter(Boolean);
  return dedupePoints3d(points);
};

const buildObstaclePoints3d = (obstruction = []) =>
  obstruction
    .map((obs) => dedupePoints3d(Array.isArray(obs?.polygon) ? obs.polygon : []))
    .filter((points) => points.length >= 3);

const clusterDimensionEntries = (entries, toleranceFt) => {
  if (!Array.isArray(entries) || !entries.length) return [];
  const sorted = [...entries].sort((a, b) => a.value - b.value);
  const clusters = [];

  sorted.forEach((entry) => {
    const last = clusters[clusters.length - 1];
    if (!last || entry.value - last.value > toleranceFt) {
      clusters.push({ ...entry });
      return;
    }

    if (entry.value >= last.value) {
      last.value = entry.value;
      last.point = entry.point;
    }
  });

  return clusters;
};

export const buildBaselineDimensionContext = (items = []) => {
  const edgesByPolygon = items.map((item) => buildPolygonEdges3d(item?.polygon || []));
  const ridgeBySegment = new Map();

  const segmentStats = edgesByPolygon.map((edges) => {
    const avgZs = edges.map((edge) => (edge.a.z + edge.b.z) / 2);
    const maxAvgZ = avgZs.length ? Math.max(...avgZs) : -Infinity;
    const minAvgZ = avgZs.length ? Math.min(...avgZs) : Infinity;
    const avgRange =
      Number.isFinite(maxAvgZ) && Number.isFinite(minAvgZ) ? maxAvgZ - minAvgZ : 0;
    return {
      maxAvgZ,
      tol: Math.max(0.03, avgRange * 0.03),
      flat: avgRange < 0.05 || avgZs.length < 2,
    };
  });

  for (let si = 0; si < edgesByPolygon.length; si++) {
    for (let sj = si + 1; sj < edgesByPolygon.length; sj++) {
      const leftStats = segmentStats[si];
      const rightStats = segmentStats[sj];
      if (!leftStats || !rightStats || leftStats.flat || rightStats.flat) continue;

      const leftEdges = edgesByPolygon[si] || [];
      const rightEdges = edgesByPolygon[sj] || [];
      for (let li = 0; li < leftEdges.length; li++) {
        for (let rj = 0; rj < rightEdges.length; rj++) {
          const leftEdge = leftEdges[li];
          const rightEdge = rightEdges[rj];
          const overlap = getCollinearOverlap(leftEdge, rightEdge);
          if (!overlap) continue;

          const leftAvg = (leftEdge.a.z + leftEdge.b.z) / 2;
          const rightAvg = (rightEdge.a.z + rightEdge.b.z) / 2;
          const leftTop = leftAvg >= leftStats.maxAvgZ - leftStats.tol;
          const rightTop = rightAvg >= rightStats.maxAvgZ - rightStats.tol;
          if (!leftTop || !rightTop) continue;

          const leftLen2 =
            (leftEdge.b.x - leftEdge.a.x) ** 2 + (leftEdge.b.y - leftEdge.a.y) ** 2;
          const rightLen2 =
            (rightEdge.b.x - rightEdge.a.x) ** 2 + (rightEdge.b.y - rightEdge.a.y) ** 2;
          if (leftLen2 < 1e-8 || rightLen2 < 1e-8) continue;

          const leftT1 = clamp01(
            ((overlap.pStart.x - leftEdge.a.x) * (leftEdge.b.x - leftEdge.a.x) +
              (overlap.pStart.y - leftEdge.a.y) * (leftEdge.b.y - leftEdge.a.y)) /
              leftLen2
          );
          const leftT2 = clamp01(
            ((overlap.pEnd.x - leftEdge.a.x) * (leftEdge.b.x - leftEdge.a.x) +
              (overlap.pEnd.y - leftEdge.a.y) * (leftEdge.b.y - leftEdge.a.y)) /
              leftLen2
          );
          const rightT1 = clamp01(
            ((overlap.pStart.x - rightEdge.a.x) * (rightEdge.b.x - rightEdge.a.x) +
              (overlap.pStart.y - rightEdge.a.y) * (rightEdge.b.y - rightEdge.a.y)) /
              rightLen2
          );
          const rightT2 = clamp01(
            ((overlap.pEnd.x - rightEdge.a.x) * (rightEdge.b.x - rightEdge.a.x) +
              (overlap.pEnd.y - rightEdge.a.y) * (rightEdge.b.y - rightEdge.a.y)) /
              rightLen2
          );

          const leftMarker = {
            a: interpolateEdgePoint(leftEdge, Math.min(leftT1, leftT2)),
            b: interpolateEdgePoint(leftEdge, Math.max(leftT1, leftT2)),
          };
          const rightMarker = {
            a: interpolateEdgePoint(rightEdge, Math.min(rightT1, rightT2)),
            b: interpolateEdgePoint(rightEdge, Math.max(rightT1, rightT2)),
          };

          if (!ridgeBySegment.has(si)) ridgeBySegment.set(si, []);
          if (!ridgeBySegment.has(sj)) ridgeBySegment.set(sj, []);
          ridgeBySegment.get(si).push(leftMarker);
          ridgeBySegment.get(sj).push(rightMarker);
        }
      }
    }
  }

  const result = new Map();
  items.forEach((_item, index) => {
    result.set(index, dedupeEdgesByKey2d(ridgeBySegment.get(index) || []));
  });
  return result;
};

export const buildBaselineMeasurementData = (item, highestEdges = []) => {
  const ring = dedupePoints3d(item?.polygon || []);
  if (ring.length < 3) {
    return {
      alignedPointsFt: [],
      alignedHighestEdgesFt: [],
      alignedObstaclesFt: [],
      alignedSetbackFt: null,
      rows: [],
      xEntries: [],
      yEntries: [],
    };
  }

  const basis = buildPlaneBasis(ring);
  const project = (point) => projectToPlane(point, basis);

  const flattenedPoints = dedupePoints2d(ring.map(project));
  const flattenedObstacles = buildObstaclePoints3d(item?.obstruction || []).map((obs) =>
    dedupePoints2d(obs.map(project))
  );
  const setback3d = buildSetbackPoints3d(item?.setbacks || []);
  const flattenedSetback =
    setback3d.length >= 3 ? dedupePoints2d(setback3d.map(project)) : null;

  const pointsFt = flattenedPoints.map((point) => ({
    x: point.x * FEET_PER_METER,
    y: point.y * FEET_PER_METER,
  }));
  const ref = pointsFt[0];
  const second = pointsFt[1] || ref;
  const angle = -Math.atan2(second.y - ref.y, second.x - ref.x);
  const alignedPointsFt = pointsFt.map((point) => rotatePoint(point, ref, angle));

  const alignedHighestEdgesFt = (Array.isArray(highestEdges) ? highestEdges : [])
    .map((edge) => {
      if (!edge?.a || !edge?.b) return null;
      const projectedA = project(edge.a);
      const projectedB = project(edge.b);
      return {
        a: rotatePoint(
          { x: projectedA.x * FEET_PER_METER, y: projectedA.y * FEET_PER_METER },
          ref,
          angle
        ),
        b: rotatePoint(
          { x: projectedB.x * FEET_PER_METER, y: projectedB.y * FEET_PER_METER },
          ref,
          angle
        ),
      };
    })
    .filter(Boolean);

  const alignedObstaclesFt = flattenedObstacles
    .map((obstacle) =>
      obstacle
        .map((point) =>
          rotatePoint(
            { x: point.x * FEET_PER_METER, y: point.y * FEET_PER_METER },
            ref,
            angle
          )
        )
        .filter(Boolean)
    )
    .filter((obstacle) => obstacle.length >= 3);

  const alignedSetbackFt =
    Array.isArray(flattenedSetback) && flattenedSetback.length >= 3
      ? flattenedSetback.map((point) =>
          rotatePoint(
            { x: point.x * FEET_PER_METER, y: point.y * FEET_PER_METER },
            ref,
            angle
          )
        )
      : null;

  const rp = alignedPointsFt[0];
  const rows = [{ poi: "RP", x: `0'0"`, y: `0'0"`, len: "-", wid: "-" }];
  for (let i = 1; i < alignedPointsFt.length; i++) {
    const point = alignedPointsFt[i];
    const dx = Number(Math.abs(point.x - rp.x).toFixed(2));
    const dy = Number(Math.abs(point.y - rp.y).toFixed(2));
    rows.push({
      poi: String(i),
      x: formatDistanceFtIn(dx),
      y: formatDistanceFtIn(dy),
      len: "-",
      wid: "-",
    });
  }

  alignedHighestEdgesFt.forEach((edge, index) => {
    const mx = (edge.a.x + edge.b.x) / 2;
    const my = (edge.a.y + edge.b.y) / 2;
    const len = Math.hypot(edge.b.x - edge.a.x, edge.b.y - edge.a.y);
    rows.push({
      poi: `R${index + 1}`,
      x: formatDistanceFtIn(Math.abs(mx - rp.x)),
      y: formatDistanceFtIn(Math.abs(my - rp.y)),
      len: formatDistanceFtIn(len),
      wid: "-",
    });
  });

  alignedObstaclesFt.forEach((obstacle, index) => {
    const bounds = getBoundsFromPoints(obstacle);
    if (!bounds) return;
    const cx = obstacle.reduce((sum, point) => sum + point.x, 0) / obstacle.length;
    const cy = obstacle.reduce((sum, point) => sum + point.y, 0) / obstacle.length;
    rows.push({
      poi: `O${index + 1}`,
      x: formatDistanceFtIn(Math.abs(cx - rp.x)),
      y: formatDistanceFtIn(Math.abs(cy - rp.y)),
      len: formatDistanceFtIn(bounds.maxX - bounds.minX),
      wid: formatDistanceFtIn(bounds.maxY - bounds.minY),
    });
  });

  const xCandidates = [];
  const yCandidates = [];
  alignedPointsFt.forEach((point, index) => {
    if (index === 0) return;
    const dx = Math.abs(point.x - rp.x);
    const dy = Math.abs(point.y - rp.y);
    if (dx >= MIN_DIM_FT) xCandidates.push({ value: dx, point });
    if (dy >= MIN_DIM_FT) yCandidates.push({ value: dy, point });
  });

  return {
    alignedPointsFt,
    alignedHighestEdgesFt,
    alignedObstaclesFt,
    alignedSetbackFt,
    rows,
    xEntries: clusterDimensionEntries(xCandidates, DIMENSION_CLUSTER_TOL_FT),
    yEntries: clusterDimensionEntries(yCandidates, DIMENSION_CLUSTER_TOL_FT),
  };
};

export const feetPointToInches = (point) => ({
  x: point.x * INCHES_PER_FOOT,
  y: point.y * INCHES_PER_FOOT,
});

export const formatBaselineDistance = formatDistanceFtIn;
